import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Copy, Eye, MoreHorizontal, Pencil, Trash2 } from "lucide-react";
import type { CampaignListItem } from "@multi-chat/shared";
import { Button } from "../../components/ui/button/index.js";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu/index.js";
import { AlertDialog, AlertDialogDescription, AlertDialogTitle } from "../../components/ui/alert-dialog/index.js";
import {
  contactsModalFooterClass,
  contactsModalHeaderClass,
} from "../contacts/contacts-dialog-presentation.js";
import { campaignRowPrimaryAction, suppressCampaignRowNavigation } from "./campaign-list-table.js";
import { CampaignModalContent } from "./CampaignModalContent.js";

export function CampaignRowActionsMenu({
  item,
  canDelete,
  busy,
  onOpen,
  onCopyId,
  onDelete,
}: {
  item: CampaignListItem;
  canDelete: boolean;
  busy: boolean;
  onOpen: () => void;
  onCopyId: () => void;
  onDelete: () => Promise<void>;
}): React.JSX.Element {
  const { t } = useTranslation();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const action = campaignRowPrimaryAction(item.status);
  const primaryLabel =
    action === "edit"
      ? t("campaigns.list.rowEdit")
      : action === "results"
        ? t("campaigns.list.rowViewResults")
        : t("campaigns.list.rowOpen");
  const PrimaryIcon = action === "edit" ? Pencil : Eye;

  return (
    <>
      <div
        onClick={(event) => event.stopPropagation()}
        onPointerDown={(event) => event.stopPropagation()}
        onKeyDown={(event) => event.stopPropagation()}
      >
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="size-8 p-0"
              aria-label={t("campaigns.list.rowActions")}
              data-testid="campaign-row-menu"
              onPointerDown={() => suppressCampaignRowNavigation()}
            >
              <MoreHorizontal size={16} aria-hidden="true" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="min-w-44 p-1">
            <DropdownMenuItem
              className="py-1.5 text-[0.8125rem]"
              onSelect={() => {
                suppressCampaignRowNavigation();
                onOpen();
              }}
            >
              <PrimaryIcon size={14} aria-hidden="true" />
              {primaryLabel}
            </DropdownMenuItem>
            <DropdownMenuItem
              className="py-1.5 text-[0.8125rem]"
              onSelect={() => {
                suppressCampaignRowNavigation();
                onCopyId();
              }}
            >
              <Copy size={14} aria-hidden="true" />
              {t("campaigns.list.copyId")}
            </DropdownMenuItem>
            {canDelete ? (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="py-1.5 text-[0.8125rem] text-destructive data-[highlighted]:bg-error-muted"
                  onSelect={() => {
                    suppressCampaignRowNavigation();
                    setConfirmDelete(true);
                  }}
                >
                  <Trash2 size={14} aria-hidden="true" />
                  {t("campaigns.list.rowDelete")}
                </DropdownMenuItem>
              </>
            ) : null}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {canDelete ? (
        <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
          <div
            data-stop-row-nav=""
            onClick={(event) => event.stopPropagation()}
            onPointerDown={(event) => {
              event.stopPropagation();
              suppressCampaignRowNavigation();
            }}
            onPointerUp={(event) => event.stopPropagation()}
          >
            <CampaignModalContent>
              <div className={contactsModalHeaderClass}>
                <AlertDialogTitle>{t("campaigns.bulk.deleteTitle", { count: 1 })}</AlertDialogTitle>
                <AlertDialogDescription>
                  {t("campaigns.bulk.deleteBody", { count: 1 })}
                </AlertDialogDescription>
              </div>
              <div className={contactsModalFooterClass}>
                <Button
                  type="button"
                  variant="ghost"
                  onPointerDown={() => suppressCampaignRowNavigation()}
                  onClick={() => setConfirmDelete(false)}
                >
                  {t("common.cancel")}
                </Button>
                <Button
                  type="button"
                  variant="destructive"
                  disabled={busy}
                  data-testid="campaign-row-delete-confirm"
                  onPointerDown={() => suppressCampaignRowNavigation()}
                  onClick={(event) => {
                    event.stopPropagation();
                    suppressCampaignRowNavigation();
                    void onDelete().finally(() => setConfirmDelete(false));
                  }}
                >
                  {t("campaigns.list.rowDelete")}
                </Button>
              </div>
            </CampaignModalContent>
          </div>
        </AlertDialog>
      ) : null}
    </>
  );
}
